import {Box, MultiSelect, Radio, Select, Stack, Text} from '@mantine/core';
import React from 'react';

import {EventTriggerConfig} from '../RoutineTypes';
import {withIndefiniteArticle} from '../routineUtils';
import {FIELD_CHANGED, OBJECT_CREATED, TriggerKind, UNAVAILABLE_FEATURE_TOOLTIP} from '../triggerConstants';
import SelectableRadioCard from './SelectableRadioCard';
import {EventTypeCapabilities} from './types';

interface EventTypeSectionProps {
  capabilities: EventTypeCapabilities;
  config: Partial<EventTriggerConfig>;
  fieldOptions: Array<{value: string; label: string}>;
  getCardClassName: (selected: boolean, disabled: boolean) => string;
  nestedEventInputContainerClassName: string;
  objectTypeLabel: string;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  onTriggerKindChange: (nextTriggerKind: TriggerKind) => void;
  sectionClassName: string;
  selectDropdownClassName: string;
  triggerKind: TriggerKind | undefined;
  valuesData: Array<{value: string; label: string}>;
}

export default function EventTypeSection({
  capabilities,
  config,
  fieldOptions,
  getCardClassName,
  nestedEventInputContainerClassName,
  objectTypeLabel,
  onChange,
  onTriggerKindChange,
  sectionClassName,
  selectDropdownClassName,
  triggerKind,
  valuesData,
}: EventTypeSectionProps): JSX.Element {
  return (
    <Stack spacing={4} className={sectionClassName}>
      <Text size="sm" weight={600}>
        Trigger
      </Text>
      <Text size="xs" color="dimmed">
        When should the routine be triggered?
      </Text>
      <Radio.Group value={triggerKind ?? ''}>
        <Stack spacing="sm">
          <SelectableRadioCard
            value={OBJECT_CREATED}
            label="On creation"
            description={`Run when ${withIndefiniteArticle(objectTypeLabel.toLowerCase())} is created`}
            disabled={capabilities.eventGroupDisabled || capabilities.objectCreatedDisabled}
            showTooltip={capabilities.showObjectCreatedUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onTriggerKindChange(OBJECT_CREATED);
            }}
            cardClassName={getCardClassName(
              triggerKind === OBJECT_CREATED,
              capabilities.eventGroupDisabled || capabilities.objectCreatedDisabled
            )}
          />
          <SelectableRadioCard
            value={FIELD_CHANGED}
            label="On field change"
            description="Run when a field changes to some specified value"
            disabled={capabilities.eventGroupDisabled || capabilities.fieldChangesDisabled}
            showTooltip={capabilities.showFieldChangesUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onTriggerKindChange(FIELD_CHANGED);
            }}
            cardClassName={getCardClassName(
              triggerKind === FIELD_CHANGED,
              capabilities.eventGroupDisabled || capabilities.fieldChangesDisabled
            )}
          >
            {triggerKind === FIELD_CHANGED && (
              <Box className={nestedEventInputContainerClassName}>
                <Stack spacing="md">
                  <Select
                    label="Field"
                    placeholder="Select field"
                    value={config.field ?? ''}
                    onChange={(value) => {
                      onChange({
                        field: value ?? '',
                        to_values: [],
                      });
                    }}
                    data={fieldOptions}
                    classNames={{dropdown: selectDropdownClassName}}
                    size="sm"
                    disabled={!capabilities.canConfigureFieldChanges}
                  />
                  <MultiSelect
                    label="Changes to values"
                    placeholder="Select values"
                    description={
                      config.field == null || config.field === ''
                        ? 'Select a field to choose values'
                        : undefined
                    }
                    value={config.to_values ?? []}
                    onChange={(values) => {
                      onChange({to_values: values});
                      if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                      }
                    }}
                    data={valuesData}
                    classNames={{dropdown: selectDropdownClassName}}
                    size="sm"
                    disabled={config.field == null || config.field === ''}
                  />
                </Stack>
              </Box>
            )}
          </SelectableRadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
